/**
 * Canvas Serializer Service
 *
 * Single Source of Truth for all canvas serialization operations.
 * Handles saving, loading, and exporting canvas data.
 *
 * Key Features:
 * - Uses enlivenObjects instead of loadFromJSON to preserve system layers
 * - Consistent coordinate transformation between canvas and print-area space
 * - Asset property handling for CDN URLs
 */

import { Canvas, util, type FabricObject } from 'fabric';
import {
  isSystemLayer,
  isSystemLikeObject,
  getUserObjects,
} from './systemLayerManager';
import {
  resolveCanvasAssets,
  filterTempAssets,
  stripAssetSrcFields,
} from '@/utils/canvasAssetResolver';
import { logger } from '@/utils/logger';
import { collectTextFaces, ensureFontsLoaded } from '@/utils/canvasFonts';
import { stripPixelContext, type CanvasObjectJSON } from '@designdeck/shared';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Canvas JSON structure
 */
export interface CanvasJSON {
  version?: string;
  objects?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

/**
 * Coordinate offset for transformation
 */
export interface CoordinateOffset {
  x: number;
  y: number;
}

/**
 * Serialization options
 */
export interface SerializeOptions {
  /** Custom properties to include in serialization */
  customProperties?: string[];
  /** Whether to filter out system layers (default: true) */
  filterSystemLayers?: boolean;
  /** Coordinate offset for transformation (optional) */
  offset?: CoordinateOffset;
}

/**
 * Export options for toDataURL
 */
export interface ExportOptions {
  /** Image format */
  format?: 'png' | 'jpeg' | 'webp';
  /** Quality for jpeg/webp (0-1) */
  quality?: number;
  /** Resolution multiplier */
  multiplier?: number;
  /** Export only print area region */
  printAreaOnly?: boolean;
  /** Print area coordinates (required if printAreaOnly is true) */
  printArea?: { x: number; y: number; width: number; height: number };
}

// =============================================================================
// ASSET PROPERTIES
// =============================================================================

/**
 * Asset properties that need special handling during serialization.
 * These are also included in FABRIC_CUSTOM_PROPERTIES (config/fabricConfig.ts).
 */
export const ASSET_CUSTOM_PROPERTIES = [
  'assetId',
  'assetType',
  'originalUrl',
  /**
   * Natural size of the uploaded original when the canvas holds a reduced
   * bitmap instead (phone uploads). The saved object has to describe the
   * original, which is what every later load draws.
   */
  'originalWidth',
  'originalHeight',
  '_uploadStatus',
] as const;

// NOTE: Custom properties are now configured globally in config/fabricConfig.ts
// Fabric.js 6.x requires FabricObject.customProperties to be set before object creation.
// See: FABRIC_CUSTOM_PROPERTIES in config/fabricConfig.ts

// =============================================================================
// SANITIZATION - For Legacy Data Without Proper System Layer Names
// =============================================================================

/**
 * Sanitize canvas JSON by removing system-like objects.
 * Handles legacy data where system layers were saved without proper `name` property.
 *
 * This function uses heuristics to identify and remove:
 * - Background rects (full-canvas, non-selectable, at origin)
 * - Mockup images (non-selectable images at origin)
 * - Print area borders (transparent rects with dashed stroke)
 *
 * @param json - Canvas JSON that may contain legacy system layer data
 * @returns Cleaned JSON with only user objects
 */
export function sanitizeCanvasJsonForUserObjects(json: CanvasJSON): CanvasJSON {
  if (!json.objects || !Array.isArray(json.objects)) {
    return json;
  }

  const originalCount = json.objects.length;
  const sanitizedObjects = json.objects.filter(
    (obj) => !isSystemLikeObject(obj)
  );
  const removedCount = originalCount - sanitizedObjects.length;

  if (removedCount > 0) {
    logger.debug(
      'CanvasSerializer',
      `Sanitized: removed ${removedCount} system-like objects from ${originalCount} total`
    );
  }

  return {
    ...json,
    objects: sanitizedObjects,
  };
}

// =============================================================================
// SERIALIZATION
// =============================================================================

/**
 * Serialize canvas to JSON with asset properties and coordinate transformation.
 *
 * @param canvas - Fabric.js canvas instance
 * @param options - Serialization options
 * @returns Serialized canvas JSON
 */
/**
 * A phone edits with a reduced bitmap while the uploaded original is what
 * every later load draws (resolveCanvasAssets resolves the asset, Fabric
 * keeps the JSON's width/height over the element's own). Saved as is, the
 * reduced width/height would crop the original to its top-left corner —
 * so the JSON describes the original's footprint at the same on-canvas size.
 */
export function describeOriginalFootprint(
  liveObj: { originalWidth?: number; originalHeight?: number },
  jsonObj: Record<string, unknown>
): void {
  const { originalWidth, originalHeight } = liveObj;
  const width = jsonObj.width;
  const height = jsonObj.height;
  if (
    !originalWidth ||
    !originalHeight ||
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    width <= 0 ||
    height <= 0 ||
    (width === originalWidth && height === originalHeight)
  ) {
    return;
  }
  const scaleX = typeof jsonObj.scaleX === 'number' ? jsonObj.scaleX : 1;
  const scaleY = typeof jsonObj.scaleY === 'number' ? jsonObj.scaleY : 1;
  jsonObj.scaleX = (scaleX * width) / originalWidth;
  jsonObj.scaleY = (scaleY * height) / originalHeight;
  jsonObj.width = originalWidth;
  jsonObj.height = originalHeight;
}

/**
 * Safety net on load: an image whose element turned out larger or smaller
 * than the JSON said (an older save of a reduced bitmap, a re-rendered
 * asset) keeps its on-canvas size instead of showing a slice.
 */
export function fitObjectToElement(obj: {
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  _element?: { naturalWidth?: number; naturalHeight?: number };
  setCoords?: () => void;
}): void {
  const natural = obj._element;
  const nw = natural?.naturalWidth ?? 0;
  const nh = natural?.naturalHeight ?? 0;
  const { width = 0, height = 0 } = obj;
  if (!nw || !nh || !width || !height || (nw === width && nh === height)) {
    return;
  }
  obj.scaleX = ((obj.scaleX ?? 1) * width) / nw;
  obj.scaleY = ((obj.scaleY ?? 1) * height) / nh;
  obj.width = nw;
  obj.height = nh;
  obj.setCoords?.();
}

export function serialize(
  canvas: Canvas,
  options: SerializeOptions = {}
): CanvasJSON {
  const { filterSystemLayers: filterSysLayers = true, offset } = options;

  // 1. Get standard serialization
  // Custom properties are automatically included via FabricObject.customProperties (set in fabricConfig.ts)
  const json = canvas.toJSON() as CanvasJSON;

  if (!json.objects || !Array.isArray(json.objects)) {
    return json;
  }

  // 2. Patch image src from live DOM elements.
  //
  //    canvas.toJSON() already serialises all customProperties (assetId, assetType,
  //    _uploadStatus, etc.) — see FABRIC_CUSTOM_PROPERTIES in fabricConfig.ts.
  //    We do NOT re-copy those properties here to avoid index-mismatch bugs
  //    that could assign the wrong assetId to the wrong object.
  //
  //    The only thing toJSON() can get wrong is the image `src`: Fabric may
  //    serialise a stale blob/data URL while the live _element already points
  //    to the permanent HTTP URL (after async S3 upload). We fix that below by
  //    matching each JSON image object to its live counterpart via `name`.
  const liveObjects = canvas.getObjects();
  const liveImagesByName = new Map<
    string,
    FabricObject & {
      _element?: HTMLImageElement;
      assetType?: string;
      originalUrl?: string;
    }
  >();
  for (const obj of liveObjects) {
    if ((obj.type === 'image' || obj.type === 'Image') && obj.name) {
      liveImagesByName.set(
        obj.name,
        obj as FabricObject & {
          _element?: HTMLImageElement;
          assetType?: string;
          originalUrl?: string;
        }
      );
    }
  }

  for (const jsonObj of json.objects) {
    if (!jsonObj) continue;
    const jsonType = jsonObj.type as string;
    if (jsonType !== 'image' && jsonType !== 'Image') continue;

    const liveObj = (jsonObj.name as string)
      ? liveImagesByName.get(jsonObj.name as string)
      : undefined;
    if (!liveObj) continue;

    // Read authoritative assetId / assetType / _uploadStatus from the live
    // object, because async S3 upload may have updated them after Fabric's
    // last internal toObject() cache.
    if (liveObj.assetId !== undefined) jsonObj.assetId = liveObj.assetId;
    if (liveObj.assetType !== undefined) jsonObj.assetType = liveObj.assetType;
    if (liveObj._uploadStatus !== undefined)
      jsonObj._uploadStatus = liveObj._uploadStatus;

    describeOriginalFootprint(
      liveObj as { originalWidth?: number; originalHeight?: number },
      jsonObj
    );

    const elementSrc = liveObj._element?.src;
    if (elementSrc && !elementSrc.startsWith('blob:')) {
      if (elementSrc.startsWith('data:') && liveObj.assetType === 's3') {
        // S3 asset with stale base64 element — resolveCanvasAssets will
        // re-resolve from assetId on load; stripAssetSrcFields cleans it.
      } else {
        jsonObj.src = elementSrc;
      }
    } else if (
      elementSrc?.startsWith('blob:') &&
      liveObj.assetType === 's3' &&
      typeof liveObj.originalUrl === 'string' &&
      /^https?:/.test(liveObj.originalUrl)
    ) {
      // A phone keeps the reduced local bitmap after the upload; the saved
      // object names the uploaded original, which loads without any
      // lookup — Direct Sale has no seller token for the asset resolver.
      jsonObj.src = liveObj.originalUrl;
    }
  }

  // Debug: Objects BEFORE filtering
  logger.debug('CanvasSerializer', 'serialize() - BEFORE filter', {
    totalObjects: json.objects.length,
    objects: json.objects.map((o, i) => ({
      index: i,
      type: o.type,
      name: o.name,
      left: o.left,
      top: o.top,
      isSystemLike: isSystemLikeObject(o),
    })),
  });

  // 3. Filter system layers if requested (uses robust heuristic)
  if (filterSysLayers) {
    json.objects = json.objects.filter((obj) => {
      return !isSystemLikeObject(obj);
    });
  }

  logger.debug('CanvasSerializer', 'serialize() - AFTER filter', {
    userObjectCount: json.objects.length,
    objects: json.objects.map((o, i) => ({
      index: i,
      type: o.type,
      name: o.name,
      leftBefore: o.left,
      topBefore: o.top,
    })),
  });

  // 4. Transform coordinates if offset provided
  if (offset) {
    logger.debug('CanvasSerializer', 'serialize() - Applying offset', {
      offset,
      objectCount: json.objects.length,
    });

    json.objects = json.objects.map((obj) => {
      const canvasLeft = (obj.left as number) || 0;
      const canvasTop = (obj.top as number) || 0;

      const newLeft = canvasLeft - offset.x;
      const newTop = canvasTop - offset.y;

      logger.debug('CanvasSerializer', 'serialize() - Transform', {
        type: obj.type,
        name: obj.name,
        before: { left: canvasLeft, top: canvasTop },
        offset: { x: offset.x, y: offset.y },
        after: { left: newLeft, top: newTop },
      });

      return {
        ...obj,
        left: canvasLeft - offset.x,
        top: canvasTop - offset.y,
      };
    });
  }

  // 5. Clean up for storage. The area clip, uniform strokes and absolute
  // clip positioning are pixel context of one template: the JSON must not
  // carry them, the editor sets the clip from the template rectangle (R34).
  json.objects = json.objects.map(
    (obj) =>
      stripPixelContext(obj as CanvasObjectJSON) as Record<string, unknown>
  );
  let cleanedJson = filterTempAssets(json) as CanvasJSON;
  cleanedJson = stripAssetSrcFields(cleanedJson) as CanvasJSON;

  logger.debug('CanvasSerializer', 'serialize() - FINAL', {
    objectCount: cleanedJson.objects?.length || 0,
    objects: cleanedJson.objects?.map((o, i) => ({
      index: i,
      type: o.type,
      name: o.name,
      left: o.left,
      top: o.top,
    })),
  });

  logger.debug(
    'CanvasSerializer',
    `Serialized ${cleanedJson.objects?.length || 0} objects`
  );

  return cleanedJson;
}

/**
 * Transform coordinates from print-area space to canvas space.
 * Used when loading saved canvas data.
 *
 * @param json - Saved JSON with print-area-space coordinates
 * @param offset - Print area offset
 * @returns JSON with canvas-space coordinates
 */
export function transformToCanvasCoordinates(
  json: CanvasJSON,
  _offset: CoordinateOffset
): CanvasJSON {
  if (!json.objects || !Array.isArray(json.objects)) {
    return json;
  }

  return {
    ...json,
    objects: json.objects.map((obj) => ({
      ...obj,
      left: (obj.left as number) || 0,
      top: (obj.top as number) || 0,
    })),
  };
}

// =============================================================================
// DESERIALIZATION - KEY: Uses enlivenObjects, NOT loadFromJSON!
// =============================================================================

/**
 * Options for {@link deserializeUserObjects}.
 */
export interface DeserializeOptions {
  /**
   * Checked after every await. Returning true means the caller no longer
   * wants this restore (position switched, canvas replaced): nothing is
   * added to the canvas and [] is returned.
   */
  isStale?: () => boolean;
}

/**
 * Deserialize user objects and add them to canvas WITHOUT clearing system layers.
 *
 * This is the critical function that fixes the loadFromJSON bug.
 * Instead of using canvas.loadFromJSON() which clears ALL objects,
 * we use util.enlivenObjects() to create objects and add them manually.
 *
 * @param json - Canvas JSON with user objects
 * @param canvas - Fabric.js canvas instance
 * @param offset - Optional coordinate offset for transformation
 * @param options - Optional staleness hook, see DeserializeOptions
 * @returns Promise that resolves when objects are loaded
 */
export async function deserializeUserObjects(
  json: CanvasJSON,
  canvas: Canvas,
  offset?: CoordinateOffset,
  options: DeserializeOptions = {}
): Promise<FabricObject[]> {
  logger.debug('CanvasSerializer', 'deserializeUserObjects() - START', {
    inputObjectCount: json.objects?.length || 0,
    offset,
    inputObjects: json.objects?.map((o, i) => ({
      index: i,
      type: o.type,
      name: o.name,
      left: o.left,
      top: o.top,
    })),
  });

  if (
    !json.objects ||
    !Array.isArray(json.objects) ||
    json.objects.length === 0
  ) {
    logger.debug('CanvasSerializer', 'No objects to deserialize');
    return [];
  }

  // 1. SANITIZE: Remove any system-like objects from legacy data
  //    This is critical for handling data saved without proper name properties
  const sanitizedJson = sanitizeCanvasJsonForUserObjects(json);
  let objectsJson = sanitizedJson.objects || [];

  logger.debug(
    'CanvasSerializer',
    'deserializeUserObjects() - After sanitization',
    {
      objectCount: objectsJson.length,
    }
  );

  if (objectsJson.length === 0) {
    logger.debug('CanvasSerializer', 'No user objects after sanitization');
    return [];
  }

  // 2. Transform coordinates if offset provided
  if (offset) {
    logger.debug(
      'CanvasSerializer',
      'deserializeUserObjects() - Applying offset',
      {
        offset,
        objectCount: objectsJson.length,
      }
    );

    objectsJson = objectsJson.map((obj) => {
      // Get saved print-area coordinates
      const printAreaLeft = (obj.left as number) || 0;
      const printAreaTop = (obj.top as number) || 0;

      // Transform to canvas coordinates (add print area offset)
      const canvasLeft = printAreaLeft + offset.x;
      const canvasTop = printAreaTop + offset.y;

      logger.debug('CanvasSerializer', 'deserializeUserObjects() - Transform', {
        type: obj.type,
        name: obj.name,
        printAreaCoords: { left: printAreaLeft, top: printAreaTop },
        offset: { x: offset.x, y: offset.y },
        canvasCoords: { left: canvasLeft, top: canvasTop },
      });

      return {
        ...obj,
        left: canvasLeft,
        top: canvasTop,
      };
    });
  }

  // 3. Resolve asset URLs and load the texts' font faces before enlivening:
  //    Fabric measures text on creation, and a face still loading then is
  //    drawn (and exported into the crop) in the fallback font.
  const [resolvedJson] = await Promise.all([
    resolveCanvasAssets({
      ...json,
      objects: objectsJson,
    }),
    ensureFontsLoaded(collectTextFaces(objectsJson)),
  ]);
  const resolvedObjects = (resolvedJson as CanvasJSON).objects || [];

  // `stage` names the await boundary that dropped the restore, so the log
  // marker stays greppable while still telling the two call sites apart.
  const discard = (stage: 'assets' | 'enliven'): FabricObject[] => {
    logger.debug('CanvasSerializer', 'Discarding stale restore', {
      stage,
      objectCount: objectsJson.length,
    });
    return [];
  };

  if (options.isStale?.()) return discard('assets');

  // 4. Use enlivenObjects to create Fabric objects WITHOUT clearing canvas
  const enlivenedObjects = await util.enlivenObjects(resolvedObjects);

  if (options.isStale?.()) return discard('enliven');

  // 5. Ensure all image objects have crossOrigin set to prevent tainted canvas
  // This is critical for canvas export operations (toDataURL)
  for (const obj of enlivenedObjects) {
    // Type guard: check if object is a FabricObject with a type property
    if (
      obj &&
      typeof obj === 'object' &&
      'type' in obj &&
      (obj.type === 'image' || obj.type === 'Image')
    ) {
      const imgObj = obj as unknown as {
        _element?: HTMLImageElement;
        crossOrigin?: string | null;
      };

      // Set crossOrigin on the image element if it exists
      if (imgObj._element && !imgObj._element.crossOrigin) {
        imgObj._element.crossOrigin = 'anonymous';
      }

      // Also set on the Fabric object itself
      if (imgObj.crossOrigin !== 'anonymous') {
        imgObj.crossOrigin = 'anonymous';
      }
      fitObjectToElement(obj as Parameters<typeof fitObjectToElement>[0]);
    }
  }

  // 6. Add each object to canvas
  enlivenedObjects.forEach((obj) => {
    canvas.add(obj as FabricObject);
  });

  canvas.requestRenderAll();

  // Log final state
  logger.debug('CanvasSerializer', 'deserializeUserObjects() - END', {
    enlivenedCount: enlivenedObjects.length,
    objects: enlivenedObjects.map((o, i) => {
      const fo = o as FabricObject;
      return {
        index: i,
        type: fo.type,
        name: fo.name,
        left: fo.left,
        top: fo.top,
        hasControls: !!fo.controls,
      };
    }),
  });

  logger.debug(
    'CanvasSerializer',
    `Deserialized ${enlivenedObjects.length} user objects`
  );

  return enlivenedObjects as FabricObject[];
}

// =============================================================================
// EXPORT TO DATA URL
// =============================================================================

/**
 * Export canvas to data URL.
 *
 * Handles tainted canvas errors gracefully by returning an empty string
 * if the canvas is tainted due to CORS issues. A tainted canvas cannot
 * be exported, so we return empty string to prevent crashes.
 *
 * @param canvas - Fabric.js canvas instance
 * @param options - Export options
 * @returns Data URL string, or empty string if export fails (tainted canvas)
 */
export function toDataURL(canvas: Canvas, options: ExportOptions = {}): string {
  const {
    format = 'png',
    quality = 1,
    multiplier = 1,
    printAreaOnly = false,
    printArea,
  } = options;

  // If exporting print area only, temporarily hide system layers
  let systemLayersVisibility: Map<FabricObject, boolean> | null = null;

  if (printAreaOnly) {
    systemLayersVisibility = new Map();
    const systemLayers = canvas
      .getObjects()
      .filter((obj) => isSystemLayer(obj.name));
    systemLayers.forEach((obj) => {
      systemLayersVisibility!.set(obj, obj.visible ?? true);
      obj.visible = false;
    });
  }

  try {
    const exportOptions: Parameters<Canvas['toDataURL']>[0] = {
      format,
      quality,
      multiplier,
    };

    // If print area specified, export only that region
    if (printAreaOnly && printArea) {
      exportOptions.left = printArea.x;
      exportOptions.top = printArea.y;
      exportOptions.width = printArea.width;
      exportOptions.height = printArea.height;
    }

    try {
      const dataUrl = canvas.toDataURL(exportOptions);

      logger.debug('CanvasSerializer', `Exported to ${format}`, {
        printAreaOnly,
        width: printArea?.width,
        height: printArea?.height,
      });

      return dataUrl;
    } catch (error) {
      // Handle tainted canvas errors (CORS issues)
      if (
        (error instanceof DOMException && error.name === 'SecurityError') ||
        (error instanceof Error &&
          error.message.includes('Tainted canvases may not be exported'))
      ) {
        logger.warn(
          'CanvasSerializer',
          'Canvas is tainted due to CORS issue. Cannot export canvas. ' +
            'Ensure all images are loaded with crossOrigin="anonymous" and proper CORS headers.',
          error instanceof Error ? error : String(error)
        );

        // Return empty string - canvas cannot be exported when tainted
        // The fallback method won't work because you can't draw a tainted canvas
        return '';
      }

      // Re-throw other errors
      throw error;
    }
  } finally {
    // Restore system layer visibility
    if (systemLayersVisibility) {
      systemLayersVisibility.forEach((wasVisible, obj) => {
        obj.visible = wasVisible;
      });
      canvas.requestRenderAll();
    }
  }
}

/**
 * Export only user objects (design) without background/mockup.
 * Returns transparent PNG of the design.
 */
export function exportDesignOnly(
  canvas: Canvas,
  printArea: { x: number; y: number; width: number; height: number },
  options: Omit<ExportOptions, 'printAreaOnly' | 'printArea'> = {}
): string {
  // Hide all system layers
  const systemLayers = canvas
    .getObjects()
    .filter((obj) => isSystemLayer(obj.name));
  const originalVisibility = systemLayers.map((obj) => obj.visible);
  systemLayers.forEach((obj) => (obj.visible = false));

  // Save and clear background
  const originalBg = canvas.backgroundColor;
  canvas.backgroundColor = 'transparent';

  try {
    return canvas.toDataURL({
      format: options.format || 'png',
      quality: options.quality || 1,
      multiplier: options.multiplier || 1,
      left: printArea.x,
      top: printArea.y,
      width: printArea.width,
      height: printArea.height,
    });
  } finally {
    // Restore
    systemLayers.forEach((obj, i) => {
      const wasVisible = originalVisibility[i];
      if (wasVisible !== undefined) {
        obj.visible = wasVisible;
      }
    });
    canvas.backgroundColor = originalBg;
    canvas.requestRenderAll();
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if canvas has any saveable user objects.
 */
export function hasDesignObjects(canvas: Canvas): boolean {
  return getUserObjects(canvas).length > 0;
}

/**
 * Get count of user objects on canvas.
 */
export function getUserObjectCount(canvas: Canvas): number {
  return getUserObjects(canvas).length;
}
